import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import colors from '../../config/colors';

const SearchableSelectQuestion = ({ question, value, onChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(question.options || []);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Debug: Check if options are available
  useEffect(() => {
    console.log('SearchableSelectQuestion mounted');
    console.log('Question ID:', question.id);
    console.log('Options count:', question.options?.length || 0);
    console.log('First 3 options:', question.options?.slice(0, 3));
  }, [question.id, question.options]);

  // Initialize selected option from value
  useEffect(() => {
    if (value && question.options) {
      const option = question.options.find(opt => opt.value === value);
      setSelectedOption(option);
      setSearchTerm(option?.label || '');
    }
  }, [value, question.options]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(question.options || []);
      return;
    }

    const term = searchTerm.toLowerCase();
    const matches = (question.options || []).filter(option =>
      option.label.toLowerCase().includes(term)
    );
    setFilteredOptions(matches);
  }, [searchTerm, question.options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Restore selected option text if user clicked away
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Clear search term to show all options
    setSearchTerm('');
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm(option.label);
    onChange(question.id, option.value);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredOptions.length === 1) {
      handleOptionSelect(filteredOptions[0]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    }
  };

  return (
    <div style={{ width: '100%' }} ref={dropdownRef}>
      {/* Question Text */}
      {question.question && (
        <label style={{
          display: 'block',
          fontSize: '18px',
          fontWeight: '600',
          color: colors.textPrimary,
          marginBottom: '8px'
        }}>
          {question.question}
        </label>
      )}
      
      {/* Subtitle */}
      {question.subtitle && (
        <p style={{
          fontSize: '14px',
          color: colors.textSecondary,
          marginBottom: '12px'
        }}>
          {question.subtitle}
        </p>
      )}
      
      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none'
        }}>
          <Search size={18} color={colors.textSecondary} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={question.placeholder || 'Search...'}
          style={{
            width: '100%',
            padding: '12px 40px 12px 40px',
            fontSize: '16px',
            border: `2px solid ${isOpen ? colors.brandPrimary : colors.borderLight}`,
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.2s',
            background: colors.bgCard,
            cursor: 'text'
          }}
        />

        <div style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none'
        }}>
          <ChevronDown 
            size={18} 
            color={colors.textSecondary}
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && filteredOptions && filteredOptions.length >= 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          maxHeight: '300px',
          overflowY: 'auto',
          background: colors.bgCard,
          border: `2px solid ${colors.brandPrimary}`,
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: colors.textSecondary,
              fontSize: '14px'
            }}>
              No matches found
            </div>
          ) : (
            <>
              {/* Helper text */}
              {filteredOptions.length > 0 && !selectedOption && (
                <div style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  color: colors.textSecondary,
                  borderBottom: `1px solid ${colors.borderLight}`,
                  background: '#F8F9FA'
                }}>
                  {filteredOptions.length === 1 
                    ? '↵ Press Enter to select' 
                    : `${filteredOptions.length} options - click to select`}
                </div>
              )}
              {filteredOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOptionSelect(option)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  textAlign: 'left',
                  border: 'none',
                  background: selectedOption?.value === option.value 
                    ? colors.bgAccent 
                    : 'transparent',
                  cursor: 'pointer',
                  fontSize: '15px',
                  color: colors.textPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background 0.15s',
                  borderBottom: `1px solid ${colors.borderLight}`
                }}
                onMouseEnter={(e) => {
                  if (selectedOption?.value !== option.value) {
                    e.target.style.background = colors.bgAccent;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption?.value !== option.value) {
                    e.target.style.background = 'transparent';
                  }
                }}
              >
                <span>{option.label}</span>
                {selectedOption?.value === option.value && (
                  <Check size={18} color={colors.brandPrimary} />
                )}
              </button>
            ))}
            </>
          )}
        </div>
      )}

      {/* Helper text */}
      {question.helper && (
        <p style={{
          marginTop: '8px',
          fontSize: '14px',
          color: colors.textSecondary,
          fontStyle: 'italic'
        }}>
          {question.helper}
        </p>
      )}
    </div>
  );
};

export default SearchableSelectQuestion;
